import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link,
  useToast,
  Icon,
  Flex,
  Image,
  Badge,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaUserPlus } from "react-icons/fa";
import axios from "axios";
import { REGISTER_URL } from "../services/api";
import { useNavigate } from "react-router-dom";

const SignupPage = ({ onSwitch }) => {
  const [form, setForm] = useState({
    companyName: "",
    email: "",
    password: "",
    name: "",
    department: "",
    designation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSignup = async () => {
    if (!form.tenantSlug || !form.email || !form.password || !form.name) {
      return toast({
        title: "Missing Fields",
        description: "Tenant slug, name, email and password are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (form.password.length < 6) {
      return toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);

    try {
      const response = await axios.post(REGISTER_URL, {
        tenantSlug: form.tenantSlug,
        email: form.email,
        name: form.name,
        password: form.password,
        department: form.department || undefined,
        designation: form.designation || undefined,
        roles: ["NOVATRA_USER"], // Default role
      });

      if (response.data.success) {
        toast({
          title: "Registration Successful",
          description: "Your account has been created. Please login.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Switch to login page after short delay
        setTimeout(() => {
          onSwitch();
        }, 1500);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      toast({
        title: "Registration Failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex height="100vh" align="center" justify="center" bg="gray.100">
      <Flex
        w={{ base: "100%", md: "90%", lg: "80%" }}
        maxW="6xl"
        boxShadow="2xl"
        rounded="2xl"
        overflow="hidden"
        bg="white"
        flexDir={{ base: "column", md: "row" }}
      >
        {/* Left Side: Branding (consistent) */}
        <Flex
          bgColor="#0B4885"
          color="white"
          flex="1"
          p={10}
          direction="column"
          justify="center"
          align="center"
          borderTopLeftRadius={{ base: "2xl", md: "2xl" }}
          borderBottomLeftRadius={{ base: "2xl", md: "2xl" }}
        >
          <Image
            src="/image.png"
            boxSize="80px"
            mb={4}
            alt="Novatra - “Bright. Seamless. Secure.”"
          />
          <Heading fontSize="2xl" mb={2} textAlign="center">
            Join Novatra - “Bright. Seamless. Secure.”
          </Heading>
          <Text fontSize="sm" textAlign="center">
            Register to access powerful HR automation and insights.
          </Text>
        </Flex>

        {/* Right Side: Sign Up Form */}
        <Box
          flex="1"
          p={{ base: 8, md: 12 }}
          bg={useColorModeValue("gray.50", "gray.800")}
          rounded={{ base: "2xl", md: "2xl" }}
          overflowY="auto"
          maxH={{ base: "auto", md: "calc(100vh - 64px)" }}
        >
          <Stack spacing={5}>
            <Stack spacing={1} align="center">
              <Icon as={FaUserPlus} boxSize={7} color="teal.500" />
              <Heading
                size="md"
                color={useColorModeValue("teal.600", "teal.300")}
              >
                Sign Up
              </Heading>
              <Badge colorScheme="teal" fontSize="0.7em">
                New User Registration
              </Badge>
              <Text
                fontSize="sm"
                color={useColorModeValue("gray.500", "gray.400")}
                textAlign="center"
              >
                Fill in the details below to create your account
              </Text>
            </Stack>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Company Name</FormLabel>
              <Input
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                placeholder="e.g. Kaan Corporation"
                autoComplete="organization"
              />
              <Text
                fontSize="xs"
                color={useColorModeValue("gray.500", "gray.400")}
                mt={1}
              >
                Your company's name (we'll auto-create your account)
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Full Name</FormLabel>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="John Doe"
                autoComplete="name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Work Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
                autoComplete="email"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Password</FormLabel>
              <Input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <Text
                fontSize="xs"
                color={useColorModeValue("gray.500", "gray.400")}
                mt={1}
              >
                Minimum 6 characters
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Department (Optional)</FormLabel>
              <Input
                name="department"
                value={form.department}
                onChange={handleChange}
                placeholder="e.g. Sales, HR"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Designation (Optional)</FormLabel>
              <Input
                name="designation"
                value={form.designation}
                onChange={handleChange}
                placeholder="e.g. Manager, Executive"
              />
            </FormControl>

            <Button
              colorScheme="teal"
              onClick={handleSignup}
              isLoading={isLoading}
              loadingText="Creating Account..."
              size="lg"
            >
              Create Account
            </Button>

            <Text fontSize="sm" textAlign="center">
              Already registered?{" "}
              <Link color="teal.500" fontWeight="medium" onClick={onSwitch}>
                Login here
              </Link>
            </Text>
          </Stack>
        </Box>
      </Flex>
    </Flex>
  );
};

export default SignupPage;
